import { useEffect, useState } from "react";
import useAnalyticsService from "../../services/AnalyticsService";
import useExperienceService from "../../services/ExperienceService";
import { ExperienceAnalyticsSummary } from "../../types/Analytics";
import { CompanySummary } from "../../types/Company";
import LoadingEllipsis from "../LoadingEllipsis";
import ExperienceAnalyticsBlock from "./ExperienceAnalyticsBlock";

interface CompanyAnalyticsByExperienceBlockProps {
  company: CompanySummary;
}

export default function CompanyAnalyticsByExperienceBlock({
  company,
}: CompanyAnalyticsByExperienceBlockProps) {
  const { getExperiencesForCompany } = useExperienceService();

  const { getExperienceSummary } = useAnalyticsService();

  const [experiences, setExperiences] = useState<
    ExperienceAnalyticsSummary[] | null
  >(null);

  const [isLoading, setIsLoading] = useState(false);

  const loadExperiencesAndAnalytics = async () => {
    setIsLoading(true);
    try {
      const experiences = await getExperiencesForCompany(company.id);

      const analyticsResults = await Promise.allSettled(
        experiences.map((experience) =>
          getExperienceSummary(company.id, experience.id)
        )
      );

      const successfulAnalytics = analyticsResults
        .map((result, index) => {
          if (result.status === "fulfilled") {
            return result.value;
          } else {
            console.warn(
              `Failed to load analytics for experience ${experiences[index].id}:`,
              result.reason
            );
            return null; // Return null for failed promises
          }
        })
        .filter(Boolean); // Remove nulls

        // Get successful analytics where not null.
        const finalResults = successfulAnalytics.filter(
          (result) => result !== null
        ) as ExperienceAnalyticsSummary[];


      setExperiences(finalResults);
    } catch (error) {
      console.error("Error loading experiences:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadExperiencesAndAnalytics();
  }, [company.id]);

  return (
    <section className="my-4">
      <h2 className="text-xl">{company.name}</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {isLoading ? (
          <LoadingEllipsis />
        ) : (
          experiences?.map((experience) => (
            <ExperienceAnalyticsBlock
              experience={experience}
              key={experience.experienceId}
            />
          ))
        )}

        {!isLoading && experiences?.length === 0 && (
          <div className="border-t border-cyan-900">
            <div className="text-left p-4">No data.</div>
          </div>
        )}
      </div>
    </section>
  );
}
